import { FarmConfig } from 'components/KodiakFarms/types'
import { useCallback, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { KodiakFarm } from 'types/farms'

import { changeLockTime } from '../actions'

export const MULTIPLIER_PRECISION = 10n ** 18n

type FarmTypes = KodiakFarm | FarmConfig

type UseLockTimeResult = {
  lockTime: number
  setLockTime: (value: number) => void
}

// Type guard to check if the farm is FarmConfig
const isFarmConfig = (farm: FarmTypes): farm is FarmConfig => {
  return 'isLive' in farm
}

// Helper to get normalized values regardless of farm type
const getNormalizedFarmValues = (farm: FarmTypes) => {
  if (isFarmConfig(farm)) {
    return {
      lockTimeMin: parseInt(farm.lockTimeMin),
      lockTimeMax: parseInt(farm.lockTimeMax),
      lockMultiplierMax: BigInt(Math.round(farm.lockMultiplierMax * Number(MULTIPLIER_PRECISION))),
    }
  }
  return {
    lockTimeMin: farm.lockTimeMin,
    lockTimeMax: farm.lockTimeMax,
    lockMultiplierMax: BigInt(farm.lockMaxMultiplier),
  }
}

export function useLockTimeMultiplier(value: number, farm?: FarmTypes) {
  const bigIntValue = useMemo(() => BigInt(value), [value])

  return useMemo(() => {
    if (!farm) return 1

    const { lockTimeMax, lockMultiplierMax } = getNormalizedFarmValues(farm)

    const bigIntLockTimeMax = BigInt(lockTimeMax)

    if (bigIntLockTimeMax === 0n) return 1

    const lockMultiplier =
      MULTIPLIER_PRECISION + (bigIntValue * (lockMultiplierMax - MULTIPLIER_PRECISION)) / bigIntLockTimeMax

    let ret: bigint

    if (lockMultiplier > lockMultiplierMax) {
      ret = lockMultiplierMax
    } else {
      ret = lockMultiplier
    }

    return parseFloat(ret.toString()) / Number(MULTIPLIER_PRECISION)
  }, [farm, bigIntValue])
}

export default function useLockTimeValue(min = 0, max = 0): UseLockTimeResult {
  const state = useAppSelector((state) => state.farms.lockTimeValue)
  const dispatch = useAppDispatch()

  const setState = useCallback(
    (newValue: number) => {
      if (newValue < min) {
        newValue = min
      }
      if (newValue > max) {
        newValue = max
      }

      dispatch(changeLockTime(newValue))
    },
    [dispatch, min, max]
  )

  useEffect(() => {
    setState(state)
  }, [min, max, setState, state])

  return {
    lockTime: state,
    setLockTime: setState,
  }
}
